// apps/web/src/components/brand/CreateBrandDialog.tsx
import * as Dialog from "@radix-ui/react-dialog";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useCreateBrand, slugify } from "@/hooks/use-brands";
import { useWizardStore } from "@/stores/wizard-store";
import { toast } from "sonner";
import type { ApiClientError } from "@/lib/api-client";
import { useTranslation } from "react-i18next";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateBrandDialog({ open, onOpenChange }: Props) {
  const { t } = useTranslation("brand");
  const { t: tCommon } = useTranslation("common");
  const [name, setName] = useState("");
  const navigate = useNavigate();
  const createBrand = useCreateBrand();
  const { openWizard } = useWizardStore();
  const slugPreview = name ? slugify(name) : "";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      const brand = await createBrand.mutateAsync({ name: name.trim() });
      toast.success(t("createDialog.toast.created", { name: brand.name }));
      onOpenChange(false);
      setName("");
      navigate(`/${brand.slug}`);
      openWizard(0);
    } catch (err) {
      const apiErr = err as ApiClientError;
      if (apiErr?.code === "CONFLICT") {
        toast.error(t("createDialog.toast.conflict"));
      } else {
        toast.error(apiErr?.message ?? t("createDialog.toast.error"));
      }
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-background p-6 shadow-lg">
          <Dialog.Title className="text-lg font-semibold">{t("createDialog.title")}</Dialog.Title>
          <Dialog.Description className="text-sm text-muted-foreground">
            {t("createDialog.description")}
          </Dialog.Description>
          <form onSubmit={handleSubmit} className="mt-4 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">{t("createDialog.nameLabel")}</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("createDialog.namePlaceholder")}
                autoFocus
                className="block w-full rounded border px-3 py-2 text-sm"
                required
              />
              {slugPreview && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("createDialog.urlPreview", { slug: slugPreview })}
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Dialog.Close asChild>
                <Button type="button" variant="outline">{tCommon("actions.cancel")}</Button>
              </Dialog.Close>
              <Button type="submit" disabled={!name.trim() || createBrand.isPending}>
                {createBrand.isPending ? t("createDialog.creating") : t("createDialog.create")}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
